import React, { useState, useEffect } from 'react';
import { GraduationCap, FileText, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Toggle to re-enable applications: set to true to restore the form
const ACCEPTING_APPLICATIONS = true;

const Preceptorship = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    school_name: '',
    program: '',
    expected_graduation: '',
    requested_start_date: '',
    requested_end_date: '',
    hours_requested: '',
    learning_objectives: '',
    terms_agreed: false
  });
  const [resume, setResume] = useState(null);
  const [enrollmentProof, setEnrollmentProof] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [isFull, setIsFull] = useState(false);

  useEffect(() => {
    axios.get(`${API}/preceptorship/status`)
      .then((res) => setIsFull(!res.data.accepting))
      .catch(() => {});
  }, []);

  const isOpen = ACCEPTING_APPLICATIONS && !isFull;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      if (fileType === 'resume') {
        setResume(file);
      } else {
        setEnrollmentProof(file);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');

    const formPayload = new FormData();
    Object.keys(formData).forEach(key => {
      formPayload.append(key, formData[key]);
    });
    
    if (resume) {
      formPayload.append('resume', resume);
    }
    if (enrollmentProof) {
      formPayload.append('enrollment_proof', enrollmentProof);
    }

    try {
      await axios.post(`${API}/preceptorship`, formPayload, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSubmitSuccess(true);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        school_name: '',
        program: '',
        expected_graduation: '',
        requested_start_date: '',
        requested_end_date: '',
        hours_requested: '',
        learning_objectives: '',
        terms_agreed: false
      });
      setResume(null);
      setEnrollmentProof(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting form:', error);
      const status = error.response?.status;
      if (status === 409) {
        setIsFull(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (status === 503) {
        setSubmitError('We could not deliver your application to our office, so it was NOT received. Please try again in a few minutes, or email your application directly to admin@cmcwv.com.');
      } else {
        setSubmitError('Your application could not be submitted and was NOT received. Please try again, or email it directly to admin@cmcwv.com.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 to-teal-600 text-white py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">
              <GraduationCap size={64} />
            </div>
          </div>
          <h1 className="text-5xl font-bold mb-6">Student Preceptorship Requests</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Clinical rotation opportunities for Nurse Practitioner students
          </p>
          {isOpen && (
            <a
              href="#application-form"
              data-testid="preceptorship-apply-now-btn"
              className="inline-flex items-center gap-2 mt-8 bg-white text-blue-700 font-semibold px-8 py-3 rounded-lg shadow-lg hover:bg-blue-50 transition-colors"
            >
              <FileText size={20} />
              Apply Now
            </a>
          )}
        </div>
      </section>

      {isOpen && (
        <div className="max-w-4xl mx-auto px-4 mt-10">
          <div data-testid="preceptorship-accepting-notice" className="bg-green-50 border-l-4 border-green-600 rounded-lg p-6 flex items-start gap-4">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-0.5" size={28} />
            <p className="text-lg font-semibold text-green-900">
              We are currently accepting a limited number of preceptorship applications. Availability is subject to placement capacity.
            </p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {submitSuccess && (
        <div className="max-w-4xl mx-auto px-4 mt-8">
          <div data-testid="preceptorship-success-message" className="bg-green-50 border-2 border-green-200 rounded-xl p-6 flex items-start space-x-4">
            <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={32} />
            <div>
              <h3 className="text-xl font-semibold text-green-900 mb-2">Request Submitted Successfully!</h3>
              <p className="text-green-800">
                Thank you for your request! A confirmation email has been sent to the address you provided.
                Our team will review your application and contact you by email.
                Please check your spam folder if you don't see our emails in your inbox.
              </p>
            </div>
          </div>
        </div>
      )}

      {ACCEPTING_APPLICATIONS && isFull && (
        <section className="pt-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div data-testid="preceptorship-full-notice" className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle size={32} className="text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Applications Full for This Term</h2>
              <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
                We have received the maximum number of preceptorship applications we can accept for this term.
                Please check back next term for new openings.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Applications Paused Notice */}
      {!ACCEPTING_APPLICATIONS && (
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div data-testid="preceptorship-paused-notice" className="bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
              <div className="bg-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle size={32} className="text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Applications Currently Paused</h2>
              <p className="text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto">
                At this time, Community Medical Center of West Volusia is not accepting new preceptorship applications.
                We have reached capacity for current placements.
                Please check back as opportunities will reopen when additional spots become available.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Introduction Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-blue-50 to-teal-50 rounded-2xl p-8 md:p-12 mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Welcome to Our Preceptorship Program</h2>
            <div className="space-y-4 text-gray-700 text-lg leading-relaxed">
              <p>
                At Community Medical Center of West Volusia, we are committed to training the next generation of 
                healthcare providers. We welcome Nurse Practitioner students for clinical rotations in both 
                <strong> Pediatrics and Internal Medicine</strong>.
              </p>
              <p>
                To ensure efficient processing and coordination, all preceptorship requests must be submitted 
                through this online form. Please provide complete and accurate information to help us evaluate 
                your request.
              </p>
            </div>

            <div className="mt-8 grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-3 text-lg">Program Details</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2"></div>
                    <span><strong>Specialties:</strong> Pediatrics & Internal Medicine</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2"></div>
                    <span><strong>Typical Duration:</strong> 8-12 weeks</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3 mt-2"></div>
                    <span><strong>Hours:</strong> 120-200 clinical hours</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-md">
                <h3 className="font-semibold text-gray-900 mb-3 text-lg">Prerequisites</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-teal-600 rounded-full mr-3 mt-2"></div>
                    <span>Current enrollment in accredited NP program</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-teal-600 rounded-full mr-3 mt-2"></div>
                    <span>Background check clearance</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-teal-600 rounded-full mr-3 mt-2"></div>
                    <span>Up-to-date vaccination records</span>
                  </li>
                  <li className="flex items-start">
                    <div className="w-2 h-2 bg-teal-600 rounded-full mr-3 mt-2"></div>
                    <span>Professional liability insurance</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 bg-blue-100 border border-blue-300 rounded-lg p-4">
              <p className="text-blue-900 text-sm">
                <strong>Note:</strong> Dates are subject to availability and preceptor schedules. 
                Approval is not guaranteed and depends on our current capacity and your qualifications.
              </p>
            </div>
          </div>

          {/* Application Form */}
          {isOpen && (
          <div id="application-form" data-testid="preceptorship-application-form" className="bg-white rounded-2xl shadow-xl p-8 md:p-12 scroll-mt-24">
            <div className="flex items-center mb-8">
              <FileText className="text-blue-600 mr-3" size={32} />
              <h2 className="text-3xl font-bold text-gray-900">Preceptorship Application</h2>
            </div>

            {submitError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                <AlertCircle className="text-red-600 flex-shrink-0 mt-1" size={24} />
                <p className="text-red-800">{submitError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="full_name"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="john.doe@university.edu"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Academic Information */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Academic Information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="school_name" className="block text-sm font-medium text-gray-700 mb-2">
                      School/University Name *
                    </label>
                    <input
                      type="text"
                      id="school_name"
                      name="school_name"
                      value={formData.school_name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="University of Florida"
                    />
                  </div>

                  <div>
                    <label htmlFor="program" className="block text-sm font-medium text-gray-700 mb-2">
                      Program *
                    </label>
                    <select
                      id="program"
                      name="program"
                      value={formData.program}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Program</option>
                      <option value="Family Nurse Practitioner">Family Nurse Practitioner</option>
                      <option value="Adult-Gerontology NP">Adult-Gerontology NP</option>
                      <option value="Pediatric Nurse Practitioner">Pediatric Nurse Practitioner</option>
                      <option value="Psychiatric Mental Health NP">Psychiatric Mental Health NP</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="expected_graduation" className="block text-sm font-medium text-gray-700 mb-2">
                      Expected Graduation Date *
                    </label>
                    <input
                      type="date"
                      id="expected_graduation"
                      name="expected_graduation"
                      value={formData.expected_graduation}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Clinical Rotation Details */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Clinical Rotation Details</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="requested_start_date" className="block text-sm font-medium text-gray-700 mb-2">
                      Requested Start Date *
                    </label>
                    <input
                      type="date"
                      id="requested_start_date"
                      name="requested_start_date"
                      value={formData.requested_start_date}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Subject to availability</p>
                  </div>

                  <div>
                    <label htmlFor="requested_end_date" className="block text-sm font-medium text-gray-700 mb-2">
                      Requested End Date *
                    </label>
                    <input
                      type="date"
                      id="requested_end_date"
                      name="requested_end_date"
                      value={formData.requested_end_date}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Subject to availability</p>
                  </div>

                  <div>
                    <label htmlFor="hours_requested" className="block text-sm font-medium text-gray-700 mb-2">
                      Number of Clinical Hours Requested *
                    </label>
                    <input
                      type="number"
                      id="hours_requested"
                      name="hours_requested"
                      value={formData.hours_requested}
                      onChange={handleChange}
                      required
                      min="40"
                      max="500"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="120"
                    />
                  </div>
                </div>

                <div className="mt-6">
                  <label htmlFor="learning_objectives" className="block text-sm font-medium text-gray-700 mb-2">
                    Brief Description of Learning Objectives or Specific Interests *
                  </label>
                  <textarea
                    id="learning_objectives"
                    name="learning_objectives"
                    value={formData.learning_objectives}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Please describe your learning goals, areas of interest, and what you hope to gain from this clinical rotation..."
                  ></textarea>
                </div>
              </div>

              {/* File Uploads */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Required Documents</h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Resume/CV * (PDF or DOC, max 5MB)
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 px-4 py-3 bg-blue-50 text-blue-600 rounded-lg border-2 border-blue-200 hover:bg-blue-100 cursor-pointer transition-colors">
                        <Upload size={20} />
                        <span className="font-medium">Choose File</span>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => handleFileChange(e, 'resume')}
                          className="hidden"
                          required
                        />
                      </label>
                      {resume && (
                        <span className="text-sm text-gray-600">
                          {resume.name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Proof of Enrollment or Other Documents (Optional)
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2 px-4 py-3 bg-gray-50 text-gray-700 rounded-lg border-2 border-gray-200 hover:bg-gray-100 cursor-pointer transition-colors">
                        <Upload size={20} />
                        <span className="font-medium">Choose File</span>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={(e) => handleFileChange(e, 'proof')}
                          className="hidden"
                        />
                      </label>
                      {enrollmentProof && (
                        <span className="text-sm text-gray-600">
                          {enrollmentProof.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms Agreement */}
              <div className="pb-6">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="terms_agreed"
                    checked={formData.terms_agreed}
                    onChange={handleChange}
                    required
                    className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">
                    I agree to provide all required documentation (background check, vaccination records, 
                    professional liability insurance) and understand that approval is not guaranteed and 
                    is subject to preceptor availability and program capacity. *
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.terms_agreed}
                  data-testid="preceptorship-submit-btn"
                  className="w-full bg-gradient-to-r from-blue-600 to-teal-600 text-white py-4 px-8 rounded-lg font-semibold text-lg hover:from-blue-700 hover:to-teal-700 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                >
                  {isSubmitting ? (
                    <span>Submitting...</span>
                  ) : (
                    <>
                      <GraduationCap size={24} />
                      <span>Submit Preceptorship Request</span>
                    </>
                  )}
                </button>
                <p className="text-center text-sm text-gray-500 mt-3">
                  You will receive a confirmation email after submitting
                </p>
              </div>
            </form>
          </div>
          )}

          {/* Contact Information */}
          <div className="mt-12 bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Questions?</h3>
            <p className="text-gray-700 mb-4">
              For questions about our preceptorship program, please contact:
            </p>
            <a 
              href="mailto:admin@cmcwv.com" 
              className="inline-block text-blue-600 hover:text-blue-700 font-semibold text-lg"
            >
              admin@cmcwv.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Preceptorship;
